// 需求文档： https://maimai.feishu.cn/wiki/wikcnIgLWZqJ3oHhvr1bcKO4Y9b
import { useRef, useEffect, useMemo } from 'react';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useInfiniteScroll } from 'ahooks';
import { isEmpty } from 'lodash';
import { Toast } from '@shared/h5/components';
import CompanyCard from '@/modules/profile/components/company-card';
import ContactCard from '@/modules/profile/components/contact-card';
import ExtCard from '@/modules/profile/components/ext-card';
import fetchData, { DataResponse, fetchDataByClient, getHotArticle, Article } from '@/modules/profile/models/model';
import absoluteUrl from '@/common/utils/absolute-url';
import { Layout, Link } from '@/common/components';
import { logout } from '@/common/utils/with-auth-server-side-props';

import styles from '@/modules/profile/styles/index.module.scss';

const { Content, Sider } = Layout;

export const getServerSideProps: GetServerSideProps<{
  data: DataResponse;
  isMobile: boolean;
  href: string;
  ekey: string;
  isLogin: boolean;
  articles: Article[];
  error?: boolean;
}> = async (context) => {
  const { ekey } = context.query as Record<string, string | undefined>;
  const userAgent = context.req.headers['user-agent'];
  const { cookies } = context.req;
  const { origin } = absoluteUrl(context.req);
  const href = origin + context.resolvedUrl;

  const isMobile = /Mobi|Android|iPhone/i.test(userAgent) || /m.maimai.cn/.test(origin);

  try {
    const [{ data }, { articles }] = await Promise.all([
      fetchData(ekey, cookies.u, cookies.access_token),
      getHotArticle(),
    ]);

    if (data?.result !== 'ok') {
      return {
        props: {
          error: true,
          isMobile,
          href,
          ekey,
          isLogin: false,
          articles: [],
          data: null,
        },
      };
    }
    const isLogin = Boolean(cookies.u && cookies.access_token) && !isEmpty(data.origin_contacts);

    if (!isLogin) {
      // 清理cookie
      logout(context);
    }
    // 增加服务端渲染缓存头
    context.res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');

    return { props: { data, isMobile, href, ekey, isLogin, articles } };
  } catch (error) {
    return {
      props: {
        error: true,
        isMobile,
        href,
        ekey,
        isLogin: false,
        articles: [],
        data: null,
      },
    };
  }
};

async function getLoadMoreList(ekey: string, nextPage?: number, isLogin?: boolean) {
  let page = 2;
  if (nextPage) {
    page = nextPage;
  }
  // 未登录限制加载3页
  if (!isLogin && page > 3) {
    Toast.show('登录查看更多');
    return Promise.reject();
  }

  try {
    const data = await fetchDataByClient(ekey, page);
    return { nextPage: page + 1, ...data, isNoMore: !data?.list?.length };
  } catch (error) {
    return Promise.reject(error);
  }
}

export default function Profile(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { data, isMobile, href, ekey, isLogin, articles, error } = props;

  // 如果有错误，显示兜底页面
  if (error) {
    return (
      <Layout user={null}>
        <Content>
          <div className='flex min-h-[400px] flex-col items-center justify-center text-center'>
            <h1 className='mb-4 text-xl'>抱歉，页面暂时无法访问</h1>
            <p className='mb-4 text-gray-500'>请稍后再试或返回首页</p>
            <Link href='/' className='text-blue-500 hover:text-blue-600'>
              返回首页
            </Link>
          </div>
        </Content>
      </Layout>
    );
  }

  const refList = useRef<Document>(undefined);

  const router = useRouter();

  const {
    data: moreDate,
    loadingMore,
    noMore,
  } = useInfiniteScroll((d) => getLoadMoreList(ekey, d?.nextPage, isLogin), {
    target: refList,
    isNoMore: (d) => d?.isNoMore,
  });

  useEffect(() => {
    refList.current = document;
  }, []);

  const userData = useMemo(() => {
    const originContacts = data.origin_contacts;
    return !isEmpty(originContacts)
      ? {
          avatar: originContacts.avatar,
          name: originContacts.realname,
          position: originContacts.position,
          company: originContacts.company,
        }
      : null;
  }, [data.origin_contacts]);

  const recContacts = useMemo(() => {
    const DCOUNT = 10;
    const count = data.contacts.length < DCOUNT ? DCOUNT - data.contacts.length : 0;
    const contacts = data.rec_contacts?.slice(0, count);
    return contacts || [];
  }, [data]);

  return (
    <>
      <Head>
        <title>{data.cooperation.title}</title>
        <meta name='keywords' content={data.cooperation.keywords} />
        <meta name='description' content={data.cooperation.description} />
        {/m.maimai.cn/.test(href) ? (
          <meta name='applicable-device' content='mobile' />
        ) : (
          <>
            <meta name='mobile-agent' content={`format=html5;url=https://m.maimai.cn${router.asPath}`} />
            <meta name='applicable-device' content='pc' />
            <link
              rel='alternate'
              media='only screen and(max-width: 768px)'
              href={`https://m.maimai.cn${router.asPath}`}
            />
          </>
        )}
        <link rel='canonical' href={href} />
      </Head>
      <Layout user={userData}>
        <Content>
          <CompanyCard company={data.cooperation} />
          {data.contacts.map((contact, i) => (
            <ContactCard key={i.toString()} contact={contact} />
          ))}
          {moreDate?.list?.map((contact, i) => (
            <ContactCard key={(data.contacts.length + i).toString()} contact={contact} />
          ))}
          {/* 推荐人脉信息 */}
          {recContacts.length > 0 && (
            <>
              <div className='py-3 text-center text-xs text-[#afb1bc]'>--- 还发现以下人脉信息 ---</div>
              {recContacts.map((contact, i) => (
                <ContactCard key={(data.contacts.length + i).toString()} contact={contact} />
              ))}
            </>
          )}

          <div>
            {loadingMore && <p className={styles.loading}>正在加载...</p>}
            {noMore && <p className={styles.loading}>已显示全部内容</p>}
          </div>
        </Content>
        {!isMobile && data.others?.length ? (
          <Sider>
            <ExtCard dataSource={data.others} />
            {!!articles?.length && (
              <div className='mt-5 rounded-lg bg-white px-6 py-2 text-[#15161F]'>
                <div className='text-sm font-bold leading-10'>大家在看</div>
                <ul>
                  {articles?.slice(0, 5).map((item) => (
                    <li className='cursor-pointer truncate text-sm leading-10' key={item.id}>
                      <Link href={`/article/detail?fid=${item.id}&efid=${item.efid}`}>{item.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Sider>
        ) : null}
      </Layout>
    </>
  );
}
